/**
 * D-5 (2026-06-02) — scenario audio cue map.
 *
 * Wires the per-location D-4 sceneMap to YouTube cue ids defined in
 * `seed/canonical/audio/youtube_cues.json` v1.1.0:
 *
 *   - libya_tobruk_ambient        — quiet briefing-style underscore
 *   - the_island_ambient          — Crab Key beach motif
 *   - ben_gazi_office_ambient     — tense villain office
 *   - cell_block_area_ambient     — suspenseful capture
 *   - control_room_lab_ambient    — Bond Theme climactic
 *   - underground_missile_facility_ambient — reactor-climax
 *
 * Endings crossfade to one of two pre-existing stings:
 *
 *   - victory + rescue_victory_sub_route → mission_complete
 *   - pyrrhic_victory_death + death + catastrophic_failure → mission_failure
 *
 * Transition behavior:
 *
 *   - BriefingView mount       → play(BRIEFING_CUE_ID)
 *   - Paragraph location change → crossfadeTo(newCue, STING_CROSSFADE_MS)
 *   - Unmapped paragraph        → no cue change (previous keeps playing)
 *   - EndingView mount          → crossfadeTo(stingCue, STING_CROSSFADE_MS)
 *   - ScenarioRunner unmount    → fadeOut(UNMOUNT_FADE_MS)
 *
 * If a cue's video_id is null (post-V11 m_office_briefing-style scaffold
 * state), YouTubeTheaterPlayer fires the existing onUnavailable callback
 * exactly once per session — the audio drawer surfaces this to the user.
 *
 * Volume / mute preferences persist via localStorage:
 *   - `briefing-room:audio-muted` (boolean string)
 *   - `briefing-room:audio-volume` (0–100 integer string)
 */

import type { SceneLocationId } from './sceneMap';
import type { EndingType } from '../../types/scenario';

export const BRIEFING_CUE_ID = 'libya_tobruk_ambient';

export const LOCATION_TO_CUE_ID: Record<SceneLocationId, string> = {
  libya_tobruk: 'libya_tobruk_ambient',
  the_island: 'the_island_ambient',
  ben_gazi_office: 'ben_gazi_office_ambient',
  cell_block_area: 'cell_block_area_ambient',
  control_room_lab: 'control_room_lab_ambient',
  underground_missile_facility: 'underground_missile_facility_ambient',
};

export const ENDING_TO_STING_CUE_ID: Record<EndingType, string> = {
  victory: 'mission_complete',
  rescue_victory_sub_route: 'mission_complete',
  pyrrhic_victory_death: 'mission_failure',
  death: 'mission_failure',
  catastrophic_failure: 'mission_failure',
};

export const AMBIENT_FADE_MS = 1500;
export const STING_CROSSFADE_MS = 1200;
export const UNMOUNT_FADE_MS = 2000;
export const MUTE_FADE_MS = 400;

export const AUDIO_MUTED_KEY = 'briefing-room:audio-muted';
export const AUDIO_VOLUME_KEY = 'briefing-room:audio-volume';
export const DEFAULT_AUDIO_VOLUME = 60;

export function readMutePreference(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(AUDIO_MUTED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeMutePreference(muted: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AUDIO_MUTED_KEY, muted ? 'true' : 'false');
  } catch {
    // quota / privacy mode — degrade silently
  }
}

export function readVolumePreference(): number {
  if (typeof window === 'undefined') return DEFAULT_AUDIO_VOLUME;
  try {
    const raw = window.localStorage.getItem(AUDIO_VOLUME_KEY);
    if (raw === null) return DEFAULT_AUDIO_VOLUME;
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return DEFAULT_AUDIO_VOLUME;
    return Math.max(0, Math.min(100, parsed));
  } catch {
    return DEFAULT_AUDIO_VOLUME;
  }
}

export function writeVolumePreference(volume: number): void {
  if (typeof window === 'undefined') return;
  try {
    const clamped = Math.max(0, Math.min(100, Math.round(volume)));
    window.localStorage.setItem(AUDIO_VOLUME_KEY, String(clamped));
  } catch {
    // quota / privacy mode — degrade silently
  }
}
